import { useMutation } from '@tanstack/react-query';
import { UpdateOrganizationAdminNotificationEmailsDto } from 'models/organization/dto/organization.dto';
import { useApiClient } from '../../../apiClient';
import { useSelectedOrganization } from './useSelectedOrganization';

// Explicit recipient list for admin-notification emails. Hits
// POST :id/admin-notification-emails. The endpoint returns only the saved list
// ({ adminNotificationEmails }) — not the full aggregate — so we patch it into
// the cached selectedOrganization locally. Mirrors
// useUpdateOrganizationCourtPayLaterEnabled in shape and intent.
//
// The response list is the NORMALIZED one (trimmed, lowercased, deduped), not
// what we sent, so writing it back to the cache is what keeps the editor in
// sync with what the server actually stored.
export const useUpdateOrganizationAdminNotificationEmails = (
    organizationId: string,
    onSuccess: () => void,
) => {
    const apiClient = useApiClient();
    const { selectedOrganization, setSelectedOrganization } = useSelectedOrganization();

    return useMutation({
        mutationKey: ['updateOrganizationAdminNotificationEmails', organizationId],
        mutationFn: (body: UpdateOrganizationAdminNotificationEmailsDto) =>
            apiClient.fetchApi<{ adminNotificationEmails: string[] }, UpdateOrganizationAdminNotificationEmailsDto>({
                method: 'POST',
                endpoint: `organizations/${organizationId}/admin-notification-emails`,
                body,
            }),
        onSuccess: (data) => {
            onSuccess();
            // Only update the selected-org cache when the response is for the
            // org currently in view — defensive against the operator switching
            // orgs while the mutation is in flight.
            if (selectedOrganization && organizationId === selectedOrganization.id) {
                setSelectedOrganization({
                    ...selectedOrganization,
                    adminNotificationEmails: data.adminNotificationEmails,
                });
            }
        },
    });
};
